import { Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';

import { setSiteReadyForPickup } from '../../redux/slices/sites/sitesSlice';
import { useDispatch } from '../../redux/store';
import { DatePicker } from '../datePicker/DatePicker';

type Props = {
  open: boolean;
  onClose: VoidFunction;
  siteId?: string;
  // Current readiness + scheduled pickup date (ISO) so the dialog opens reflecting
  // the site's actual state.
  currentReady?: boolean;
  currentPickupDate?: string | null;
};

// Manages a job site's "ready for pickup" state. The Ready checkbox is the flag;
// checking it enables an optional pickup date (leave empty = ready but
// unscheduled), and unchecking it clears the date. Saving persists both — an
// unchecked box clears readiness and any date on the backend.
export const ReadyForPickupModal: React.FC<Props> = ({ open, onClose, siteId, currentReady, currentPickupDate }: Props) => {
  const dispatch = useDispatch();
  const [ready, setReady] = useState(true);
  const [date, setDate] = useState<Date | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Editing an already-ready site keeps its date (empty if unscheduled);
    // marking a not-yet-ready site defaults the date to one day out.
    setReady(true);
    setDate(currentReady ? (currentPickupDate ? new Date(currentPickupDate) : null) : DateTime.now().plus({ days: 1 }).startOf('day').toJSDate());
  }, [open, currentReady, currentPickupDate]);

  const handleToggleReady = (checked: boolean): void => {
    setReady(checked);
    if (!checked) setDate(null); // clearing the check clears the date
  };

  const handleSave = async (): Promise<void> => {
    if (!siteId) return;
    try {
      setSending(true);
      await dispatch(
        setSiteReadyForPickup({
          siteId,
          body: { ready_for_pickup: ready, pickup_date: ready && date ? date.toISOString() : undefined },
        }),
      ).unwrap();
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Ready for Pickup</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Mark the site ready for pickup, then optionally schedule a pickup date — leave it empty to stay ready but unscheduled.
        </Typography>
        <FormControlLabel
          control={<Checkbox checked={ready} onChange={(e) => handleToggleReady(e.target.checked)} />}
          label="Ready for pickup"
          sx={{ mb: 1 }}
        />
        <DatePicker label="Pickup Date" value={date} onChange={(value) => setDate(value || null)} withClear={ready} disabled={!ready} disablePast />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={sending}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={sending}
          startIcon={sending && <CircularProgress size={22} color="inherit" />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
